// frontend/src/components/JalaliDateInput.jsx
import React, { useState, useRef, useEffect } from 'react';

const JalaliDateInput = ({ onChange, className = '' }) => {
    
  const inputRef = useRef(null);
  const [display, setDisplay] = useState('1___/__/__');

  const EDITABLE_POS = [1, 2, 3, 5, 6, 8, 9];

  const isLeapYear = (yearStr) => {
    if (yearStr.length !== 4) return false;
    const y = parseInt(yearStr, 10);
    return (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
  };

  const parseValue = () => {
    const [y, m, d] = display.split('/');
    return {
      year: y.replace(/_/g, '') || null,
      month: m.replace(/_/g, '') || null,
      day: d.replace(/_/g, '') || null,
      formatted: display
    };
  };

  useEffect(() => {
    onChange?.(parseValue());
  }, [display, onChange]);

  const moveToNextPlaceholder = (outPos) => {
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      let pos = outPos;
      const chars = display.split('');

      while (pos < chars.length && !EDITABLE_POS.includes(pos)) pos++;
      while (pos < chars.length && chars[pos] !== '_') {
        pos++;
        while (pos < chars.length && !EDITABLE_POS.includes(pos)) pos++;
      }

      if (pos >= chars.length) {
        pos = EDITABLE_POS.find(p => chars[p] === '_') || 1;
      }

      inputRef.current.setSelectionRange(pos, pos);
    });
  };

  const isCharAllowedAt = (char, pos) => {
    if (!/[0-9]/.test(char)) return false;
    const chars = display.split('');
    const y1 = '1', y2 = chars[1], y3 = chars[2], y4 = chars[3];
    const m1 = chars[5], m2 = chars[6];
    const d1 = chars[8], d2 = chars[9];
    const year = [y1, y2, y3, y4].filter(c => c !== '_').join('');
    const month = [m1, m2].filter(c => c !== '_').join('');

    switch (pos) {
      case 1: return ['3', '4'].includes(char);
      case 2: return y2 === '3' || (y2 === '4' && char === '0');
      case 3: return y2 !== '4' || y3 !== '0' || ['0','1','2','3','4'].includes(char);
      case 5: return ['0', '1'].includes(char);
      case 6:
        if (m1 === '0') return ['1','2','3','4','5','6','7','8','9'].includes(char);
        if (m1 === '1') return ['0','1','2'].includes(char);
        return false;
      case 8:
        const isDecLeap = month === '12' && year.length === 4 && isLeapYear(year);
        return !isDecLeap || char !== '3';
      case 9:
        if (d1 !== '3') return true;
        return (month >= '07' && month <= '12' && char === '0') ||
               (month >= '01' && month <= '06' && ['0','1'].includes(char));
      default: return false;
    }
  };

  const handleInput = (e) => {
    const cursor = e.target.selectionStart;
    const inputChar = e.nativeEvent.data;

    if (!inputChar || !/[0-9]/.test(inputChar)) {
      e.preventDefault();
      return;
    }

    const pos = cursor - 1;

    if (EDITABLE_POS.includes(pos) && isCharAllowedAt(inputChar, pos)) {
      const newDisplay = display.split('');
      newDisplay[pos] = inputChar;
      setDisplay(newDisplay.join(''));
      moveToNextPlaceholder(cursor);
    } else {
      e.preventDefault();
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(cursor - 1, cursor - 1);
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const filledPositions = EDITABLE_POS.filter(p => display[p] !== '_');
      if (filledPositions.length === 0) return;
      const lastFilledPos = Math.max(...filledPositions);
      const newDisplay = display.split('');
      newDisplay[lastFilledPos] = '_';
      setDisplay(newDisplay.join(''));
      requestAnimationFrame(() => {
        inputRef.current?.setSelectionRange(lastFilledPos, lastFilledPos);
      });
    }

    if (e.key === 'Delete') {
      e.preventDefault();
      const cursor = e.target.selectionStart;
      const nextFilled = EDITABLE_POS.find(p => p >= cursor && display[p] !== '_');
      if (nextFilled !== undefined) {
        const newDisplay = display.split('');
        newDisplay[nextFilled] = '_';
        setDisplay(newDisplay.join(''));
        requestAnimationFrame(() => {
          inputRef.current?.setSelectionRange(nextFilled, nextFilled);
        });
      }
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const cursor = e.target.selectionStart;
      const prev = [...EDITABLE_POS].reverse().find(p => p < cursor);
      if (prev !== undefined) moveToNextPlaceholder(prev);
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const cursor = e.target.selectionStart;
      const next = EDITABLE_POS.find(p => p > cursor - 1);
      if (next !== undefined) moveToNextPlaceholder(next);
    }
  };

  const handleClick = () => {
    moveToNextPlaceholder(1);
  };

  const handleFocus = () => {
    moveToNextPlaceholder(1);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={display}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onMouseDown={handleClick}
      onFocus={handleFocus}
      className={`w-full font-mono text-lg text-center tracking-wider ${className}`}
      placeholder="1___/__/__"
      style={{
        letterSpacing: '0.3em',
        caretColor: '#6366f1',
        direction: 'ltr',
      }}
    />
  );
};

export default JalaliDateInput;